import React, { Component } from 'react';
import { Formik, Form } from 'formik';
import moment from 'moment';
import lodashCompact from 'lodash/fp/compact';

import capitalize from 'common/util/capitalize';
import { getHouseholdQualification } from 'common/householdApplication/prequalificationHelpers';
import { formatPrice } from 'common/formatters';

import { getListingAvailableDateRange } from 'common/listingHelpers';

import { SR_API_ACTION_HOUSEHOLD_MARK_APPLICATIONS_PENDING_LANDLORD } from 'api/actionTypes';

import { HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD } from 'common/householdConstants';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import ButtonDeprecated from 'client/components/ButtonDeprecated';
import SrFormikField, { SR_FIELD_THEME_PARTNER_FORM } from 'client/components/SrFormikField';
import SrSeparator from 'client/components/SrSeparator';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrHouseholdPrequalSummary from 'client/components/SrHouseholdPrequalSummary';

import postWithState from 'client/actions/postWithState';

function getFormFields({ householdRequirements, tenantScreeningCriteria, listing }) {
  const renterRequirementIds = lodashCompact([
    'creditScore',
    'monthlyIncome',
    'moveInDate',
    'bankruptcies',
    'evictions',
    'pets',
    listing.petsAllowed && tenantScreeningCriteria.breedRestrictions.length > 0 && 'petBreeds',
  ]);

  const formFields = renterRequirementIds.reduce(
    (memo, requirementId) => {
      if (
        householdRequirements[requirementId] &&
        !householdRequirements[requirementId].isQualified
      ) {
        let title = householdRequirements[requirementId].title;
        if (requirementId === 'creditScore') {
          title = `credit score is below ${tenantScreeningCriteria.creditScore}`;
        } else if (requirementId === 'monthlyIncome') {
          title = `monthly income is below ${formatPrice(
            listing.price * tenantScreeningCriteria.rentToIncomeRatio,
          )} (${tenantScreeningCriteria.rentToIncomeRatio}x rent)`; // eslint-disable-line max-len
        } else if (requirementId === 'moveInDate') {
          const listingAvailableDateRange = getListingAvailableDateRange({
            listing,
            tenantScreeningCriteria,
          });
          title = `move-in date is not between ${moment(listingAvailableDateRange.startDate)
            .utc()
            .format('MMM D')} - ${moment(listingAvailableDateRange.endDate)
            .utc()
            .format('MMM D')}`.toLowerCase(); // eslint-disable-line max-len
        } else if (requirementId === 'felonies') {
          title = `Felony ${tenantScreeningCriteria.feloniesDescription}`;
        } else if (requirementId === 'bankruptcies') {
          title = `Bankruptcy ${tenantScreeningCriteria.bankruptciesDescription}`;
        } else if (requirementId === 'evictions') {
          title = `Eviction ${tenantScreeningCriteria.evictionsDescription}`;
        } else if (requirementId === 'petBreeds') {
          title = 'Dog Breed not allowed';
        }

        memo.push({
          id: `mismatch${capitalize(requirementId)}Reason`,
          title: title,
          type: 'long_text',
          validations: ['required'],
        });
      }

      return memo;
    },
    [
      {
        id: 'appProNote',
        title: 'general notes (optional)',
        type: 'long_text',
      },
    ],
  );

  return formFields;
}

class SrHouseholdPendingLandlordModal extends Component {
  _onSubmitHousehold = (mismatches = {}) => {
    if (!global.confirm('Are you sure the info is correct?')) {
      return;
    }
    const { household, listingId, onClose, dispatch } = this.props;

    const { householdListingId } = household.listingApplications.find(
      ({ listing }) => listing.id === listingId,
    );

    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_MARK_APPLICATIONS_PENDING_LANDLORD,
      payload: {
        householdListingId,
        mismatches,
        sentToPartnerDate: new Date().toISOString(),
      },
      onSuccess: () => {
        onClose({
          householdId: household.id,
        });
      },
    });
  };

  render() {
    const { currentUser, household, listingId, onClose } = this.props;

    const { listing, partner, applications, activeApps } = household.listingApplications.find(
      ({ listing }) => listing.id === listingId,
    );
    const tenantScreeningCriteria = household.tenantScreeningCriteria.find(
      ({ managementCompanyName }) => managementCompanyName === listing.listingManagementCompany,
    );

    const hasMoreActiveApps = activeApps > 0;
    const householdQualification = getHouseholdQualification({
      moveInDate: household.renterMoveInDate,
      listing,
      tenantScreeningCriteria,
      renters: household.users,
      householdApplications: applications.map((application) => ({
        ...application,
        status: HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD,
      })),
      householdGuarantors: household.householdGuarantors,
      guarantorApplications: household.guarantorApplications,
      tenantAlertReports: household.tenantAlertReports,
      leadRenterId: household.leadRenter.id,
      partner,
    });
    const isHouseholdQualified = householdQualification.isQualified;
    const formFields = getFormFields({
      ...householdQualification,
      tenantScreeningCriteria,
      listing,
    });

    return (
      <SrDarkModal className="SrHouseholdPendingLandlordModal" onClose={onClose}>
        <SrModalContent width={940} dynamicHeight>
          <SrBox>
            <SrFlex direction="col" align="center" className="header">
              <SrText size="2xl" weight="bold" align="center">
                {listing.listingManagementCompany} will see this:
              </SrText>
              <SrText paddingTop={9}>Confirm it looks correct before sending.</SrText>
            </SrFlex>
            <Formik
              validate={createFormValidationFn(formFields)}
              initialValues={createInitialValues({
                formFields: formFields,
              })}
              onSubmit={this._onSubmitHousehold}
              render={(formikBag) => {
                return (
                  <Form className="content">
                    <SrBox>
                      <SrText size="2xl" weight="medium">
                        {isHouseholdQualified
                          ? 'Write a note for the partner:'
                          : 'Explain why we are sending them someone who isn’t completely qualified:'}
                      </SrText>
                      <div className="formFields">
                        {formFields.map((formField) => (
                          <SrFormikField
                            key={formField.id}
                            formField={formField}
                            formikBag={formikBag}
                            className={SR_FIELD_THEME_PARTNER_FORM}
                          />
                        ))}
                      </div>
                    </SrBox>
                    <SrBox>
                      <SrBox className="prequalSummaryBox">
                        <SrText size="2xl" weight="bold" color="green" display="block">
                          {listing.addressStreetDisplay}
                        </SrText>
                        <SrText color="grey" paddingTop={4}>
                          Submitted now
                        </SrText>
                        {hasMoreActiveApps && (
                          <SrBox marginTop="5px">
                            <SrText weight="800" fontSize="16px" color="yellowGold">
                              This household has applied to other listings
                            </SrText>
                          </SrBox>
                        )}
                        <SrSeparator />
                        <SrHouseholdPrequalSummary
                          partner={partner}
                          listing={listing}
                          currentUser={currentUser}
                          tenantScreeningCriteria={tenantScreeningCriteria}
                          householdApplications={applications.map((application) => ({
                            ...application,
                            status: HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD,
                          }))}
                          rentersQualifications={householdQualification.rentersQualifications}
                          householdRequirements={householdQualification.householdRequirements}
                          rentersVerifications={householdQualification.rentersVerifications}
                          guarantorsVerifications={householdQualification.guarantorsVerifications}
                          householdVerifications={householdQualification.householdVerifications}
                          guarantorApplications={household.guarantorApplications}
                          tenantAlertReports={household.tenantAlertReports}
                          householdListing={{
                            ...formikBag.values,
                          }}
                        />
                      </SrBox>
                      <ButtonDeprecated onClick={formikBag.handleSubmit}>
                        looks good, send now
                      </ButtonDeprecated>
                    </SrBox>
                  </Form>
                );
              }}
            />
          </SrBox>
        </SrModalContent>
      </SrDarkModal>
    );
  }
}

SrHouseholdPendingLandlordModal.displayName = 'SrHouseholdPendingLandlordModal';

export default SrHouseholdPendingLandlordModal;
